<template>
    <LayoutBlock
        ref="layoutBlock"
        :tutorial_dialog="tutorial_dialog"
        v-on:closeDialog="handleCloseDialog"
        @weightsConfirmed="weightsConfirmed"
        >
        <div class="d-flex flex-column" v-if="measures && updated">
            <div class="pl-2 pb-2 subtitle-1" v-if="conf.title" >{{conf.title}}</div>
            <v-container class="ma-0 pa-0">
                <v-row no-gutters>
                    <v-col cols="12" class="d-flex justify-end py-2" v-if="!preview">
                        <v-btn @click="openWeightSelected(measures.items)" v-if="!preview">Vedi pesi</v-btn>
                        <v-btn class="ml-4" color="primary" @click="autoFill(measures.items)">Riempimento automatico</v-btn>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                <v-col cols="12" >
                    <v-simple-table class="measure_table">
                    <template v-slot:default>
                    <thead class="expansion">
                        <tr>
                            <th class="table-text-blocks text-right">{{ data.block.conf.headers.count }}</th>
                            <th class="table-text-blocks text-right">{{ data.block.conf.headers.nom }} <br/><i class="caption">({{data.instrument_data.uom}})</i></th> 
                            <th class="table-text-blocks text-right">{{ data.block.conf.headers.weight }} </th>
                            <th class="table-text-blocks text-right">{{ data.block.conf.headers.conv }} <br/><i class="caption">({{data.instrument_data.uom}})</i></th> 
                            <th class="table-text-blocks text-right">{{ data.block.conf.headers.reading }} <br/><i class="caption">({{data.instrument_data.uom}})</i></th>
                            <th class="table-text-blocks text-right">{{ data.block.conf.headers.difference }} <br/><i class="caption">({{data.instrument_data.uom}})</i></th> 
                            <th class="table-text-blocks text-right" v-if="!is_OIML && measures.useTolerance">{{data.block.conf.headers.toleranceError}} <br/><i class="caption">({{computeToleranceType.uom}})</i></th>
                            <th class="table-text-blocks text-right" v-if="measures.useTolerance">{{data.block.conf.headers.tolerance}} <br/><i class="caption">({{computeToleranceType.uom}})</i></th>
                        </tr>
                    </thead>
                    <tr v-for="(item,index) of measures.items" :key="index">
                            <td class="table-blocks d-flex align-center">
                                <v-icon class="d-flex flex-shrink-1 flex-grow-0" v-if="index >= conf.min_readings && !preview" @click="removeRow(index)">delete</v-icon>
                                <div v-else class="d-flex mr-4"></div>
                                <div class="d-flex justify-center flex-shrink-0 flex-grow-1 mr-1">{{index + 1}}</div>
                            </td>
                            <td class="table-blocks">
                                <NumericField dense outlined 
                                    solo hide-details 
                                    :readonly="preview" 
                                    v-model="item.nom" 
                                    ></NumericField>
                            </td>
                            <td class="table-blocks text-center">
                                <v-btn @click="handleOpen(item)" v-if="!preview">scegli pesi</v-btn>
                            </td>
                            <td class="table-blocks">
                                <NumericField dense outlined
                                hide-details
                                disabled 
                                placeholder="0" 
                                v-model="item.conv"
                                ></NumericField>
                            </td>
                            <td class="table-blocks">
                                <NumericField dense outlined 
                                solo hide-details
                                :readonly="preview" 
                                @input="calculateRow(item)"
                                placeholder="0"
                                v-model="item.reading"
                                :class="readingWarning(item.reading)"
                                :precision="currentPrecision"
                                ></NumericField>
                            </td>
                            <td class="table-blocks">
                                <NumericField dense outlined 
                                hide-details
                                disabled 
                                placeholder=0
                                :negative="true"
                                v-model="item.difference"
                                ></NumericField>
                            </td>
                            <td class="table-blocks" v-if="!is_OIML && measures.useTolerance">
                                <NumericField dense outlined 
                                hide-details
                                disabled 
                                placeholder=0
                                :negative="true"
                                v-model="item.toleranceError"
                                ></NumericField>
                            </td>
                            <td class="table-blocks" v-if="measures.useTolerance">
                                <NumericField dense outlined 
                                hide-details
                                disabled 
                                placeholder=0
                                :negative="true"
                                v-model="item.tolerance"
                                ></NumericField>
                            </td>
                        </tr>
                    </template>
                    </v-simple-table>
                </v-col>
                <v-col sm="6" offset-sm="6" class="d-flex flex-grow-1 flex-shrink-0 justify-end">
                    <v-divider class="pl-4" vertical></v-divider>
                    <div class="d-flex flex-column align-self-start my-2 pl-4">
                        <v-checkbox label="Verifica conformità" :disabled="preview" v-model="measures.useTolerance"></v-checkbox>
                        <NumericField dense outlined
                            disabled
                            label="errore massimo"
                            v-model="measures.max_error"
                            :suffix="computeToleranceType.uom"
                            />
                        <NumericField dense outlined
                            label="incertezza massima"
                            disabled
                            v-model="measures.u_max"
                            :suffix="getUncertain"
                        />
                        <span v-if="measures.useTolerance" class="my-2">
                            tolleranza: <i>{{computeToleranceType.type}}, {{computeToleranceType.value}}</i>
                        </span>
                        <span v-if="measures.useTolerance">
                            <StatusIcon 
                                :status="measures.result" 
                                :tooltip_text="block_tooltip[measures.result]" />
                            {{computedStatus}}
                        </span>
                    </div>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <div class="d-flex flex-column flex-grow-1 flex-shrink-0 table-bordered ma-2">
                    <div v-if="precision_overflow" class="d-flex error--text caption"><StatusIcon status="failed" small /><b>Controlla valori e precisione letture</b></div>
                    <div v-if="checkAscendence" class="error--text caption"><v-icon small color="error">warning</v-icon><b>I valori nominali non sono crescenti</b></div>
                </div>        
                <div class="d-flex flex-grow-0 flex-shrink-1 my-2">
                    <v-btn v-if="checkStatic && !preview" color="primary" @click="addRow"><v-icon class="pa-2">add_box</v-icon>aggiungi misura</v-btn>
                </div>
            </v-row>
        </v-container>
        </div>
    </LayoutBlock>
</template>
<script>
const nerdamer = require('nerdamer/all')
import { linearity_item_scie } from '../../../utils/itemBlockSchema'
import {convertPrimaryWeights} from "../../../utils/commonUtils"
import {BaseIso} from "../../../utils/baseIso"
// import _ from 'lodash'
import LayoutBlock from "../../../components/utils/LayoutBlock"
import StatusIcon from "../../../components/utils/StatusIcon"
import Big from "big.js"

export default {
    components: {LayoutBlock, StatusIcon},
    mixins: [BaseIso],
    data(){
        return{
            currentItem: null,
            headers: [
                {text: "#", align: "left", sortable: false},
                {text: "valore nominale incertezza", align:"left", sortable: false},
                {text: "valore convenzionale incertezza", align:"left", sortable: false}
            ],
            u_max: 0,
        }
    },
    methods: {
        calculateRow(item){
            if (item.reading == null || item.reading == '') {
                item.result = 'uncomplete'
            } else {
                item.result = 'done'
            }
            if(item.reading && item.conv){
                let range = this.calcMinRange(item.reading)
                let precision = this.currentPrecision
                if(range){
                    precision = this.countDecimals(range.d)
                }
                item.difference = this.round(Big(item.reading).minus(item.conv), precision)
                if(!this.is_OIML){
                    item.toleranceError = this.handleToleranceError(item)
                }
            }
        },
        addRow(){
            let item = linearity_item_scie()
            this.measures.items.push(item)
        },
        async buildMeasures(){
            this.measures = {
                max_error: "0",
                max_diff: "0",
                u_max: "0",
                result: 'uncomplete',
                items: [],
                useTolerance: false,
                toleranceData: null
            }
            this.buildTolerances()
            let valuesFromCategory = await this.buildWithCategory()
            if(valuesFromCategory) {
                // console.log("$$ debug weight dict", weight_dict)
                for(let i = 0; i< this.data.block.conf.min_readings; i++){
                    let item = linearity_item_scie({
                        nom: valuesFromCategory[i],
                        tolerance: this.measures.tolerance
                    })
                    this.measures.items.push(item)
                }
                let weight_dict = this.prepareDict()
                for(let item of this.measures.items){
                    this.currentItem = item
                    let autoSelected = this.autoSelection(item.nom, weight_dict)
                    if(autoSelected.length > 0){
                        this.weightsConfirmed(autoSelected)
                    }

                }
            } else {
                for(let i = 0; i< this.data.block.conf.min_readings; i++){
                    let item = linearity_item_scie({
                        tolerance: this.measures.tolerance
                    }) 
                this.measures.items.push(item)
                }
            }
        },
        calculateConv(item, weights){
            item.conv =  weights.reduce((a,b) => Big(a).plus(b.conv).toString(),0)
            this.calculateUncertainty(item, weights)
            this.calculateRow(item)
            if(this.is_OIML){
                item.tolerance = this.round(this.calc_OIML(this.calcMinRange(item.conv),item.conv))
            }
        },
        handleOpen(item){
            this.currentItem = item
            this.openWeightSelection(item.nom, item.weights)
        },
        weightsConfirmed(weights){
            this.currentItem.weights = _.cloneDeep(weights)
            let items = convertPrimaryWeights(weights, _.cloneDeep(this.data.primaries), this.instrument_data.uom)
            this.currentItem.nom = items.reduce((a,b) => Big(a).plus(b.name).toString(), 0)
            this.calculateConv(this.currentItem, items)
        },
        getRepeatability(){
            let rep = null
            if(this.data.block.name === "linearity"){
                if(this.data.common_data && this.data.common_data.repeatability){
                    rep = this.data.common_data.repeatability
                }
            } else if (this.data.block.name === "linearity_before"){
                if(this.data.common_data && this.data.common_data.repeatability_before){
                    rep = this.data.common_data.repeatability_before
                }
            }
            // console.log("$$ incertezza: ripetibilità", this.data.common_data, rep)
            for (let key in rep){
                return rep[key].value
            }
            return this.round(0)
        },
        calculateUncertainty(item, weights){
            // console.log("$$ incertezza, calcolando")
            item.u_nom = weights.reduce((a,b) => Big(a).plus(b.uncertain_ext).toString(),0)
            if(this.formulas && this.formulas['formula:incertezzaBilanciaTecnica']){
                let formula_incertezza = this.formulas['formula:incertezzaBilanciaTecnica']
                let tempData = _.cloneDeep(formula_incertezza.data)
                tempData.Im = item.u_nom
                tempData.s = this.getRepeatability()
                tempData.K = formula_incertezza.data.K
                let tempResult = nerdamer(formula_incertezza.equation, tempData).evaluate().text()
                item.u_conv = this.round(tempResult, this.currentPrecision +2)
                // console.log("$$ incertezza calcolata", item.u_nom, item.u_conv)
            } else {
                item.u_conv = this.round(0)
            }
            this.calcualate_max_uncertainty()
            
        },
        calculate_max_error(){
             if(this.formulas && this.formulas['formula:massimo']){
                let formula_max = this.formulas['formula:massimo']
                let formatted_list = 0
                let diff_list = this.measures.items.map(x => this.round(Big(x.difference).abs()))
                if(!this.is_OIML){
                    formatted_list = this.measures.items.map(x => {
                        if(x.toleranceError){
                            return this.round(x.toleranceError)
                        } else {
                            return this.round(0)
                        }})
                }
                let paramName = Object.keys(formula_max.data)[0] // name of the variable, like a in stdev(a)
                let temp = this.is_OIML ? diff_list : formatted_list
                var test = formula_max.equation.replace(paramName, temp.toString())
                this.measures.max_error = nerdamer(test).evaluate().text()
                var test2 = formula_max.equation.replace(paramName, diff_list.toString())
                this.measures.max_diff = this.round(nerdamer(test2).evaluate().text())
            }
            else{
                if(!this.measures.max_error){
                    this.measures.max_error = this.round(0)
                }
                if(!this.measures.max_diff){
                    this.measures.max_diff = this.round(0)
                }
            } 
        },
        calculate_result(){
            // console.log("$$ calculating result", this.measures.result)
            for(let row of this.measures.items){
                if (row.reading == null || row.reading == '' ) {
                    return 'uncomplete'
                }
                if(this.is_OIML) {
                    if(row.difference && row.tolerance && !this.precision_overflow){
                        row.result = Big(row.difference).lte(row.tolerance) ? 'done' : 'failed'
                    } else {
                        row.result = 'failed'
                        return "failed"
                    }
                } else {
                    if(row.toleranceError && row.tolerance && !this.precision_overflow){
                        row.result = Big(row.toleranceError).lte(row.tolerance) ? 'done' : 'failed'
                    } else {
                        row.result = 'failed'
                        return "failed"
                    }
                }
            }
            for(let row of this.measures.items){
                // console.log("$$ row result", row.result, !row.result)
                if(row.result == 'failed'){
                    return "failed"
                }
            }
            return "done"
        },
        calcualate_max_uncertainty(){
            if(this.formulas && this.formulas['formula:massimo']){
                let temp = this.measures.items.map(function(x){
                    if(x.u_conv){
                        return Big(x.u_conv).abs().toString()
                    } 
                    return 0
                })
                let formula_max = this.formulas['formula:massimo']
                let paramName = Object.keys(formula_max.data)[0] // name of the variable, like a in stdev(a)
                var test = formula_max.equation.replace(paramName, temp.toString())
                let pippo = nerdamer(test).evaluate().text()
                this.measures.u_max = pippo
            } else {
                this.measures.u_max = this.round(0)
            }
        },
        saveMeasures() {
            let block = this.data.block
            this.data.common_data[block.name][block._id].value = this.measures.max_error
            this.calcBlockResult()
            this.$parent.$emit('updateMeasure', {b_index: this.data.b_index, measures: this.measures, p_idx: this.data.p_idx, p_id: this.data.p_id, common_data: this.data.common_data })
        },
        calcBlockResult() {

        },
        handleInvalidPrimaries(){
            if(this.data.primaries && this.data.invalidPrimaries){
                console.log("$$ resetting linearity")
                this.measures.items.forEach(item => {
                    item.weights.splice(0, item.weights.length)
                    item.conv = 0
                    item.u_nom = 0
                    item.u_conv = 0
                    item.difference = 0 - item.reading

                })
                this.measures.max_error = 0
                this.measures.u_max = 0
            }
        },
        handleToleranceChange(){
            for(let item of this.measures.items){
                item.toleranceError = this.handleToleranceError(item)
                if(this.is_OIML){
                    if(item.conv){
                        item.tolerance = this.round(this.calc_OIML(this.calcMinRange(item.conv),item.conv))
                    }
                } else {
                    item.tolerance = this.measures.tolerance
                }
            }
        }
        
    },
    computed: {
        checkAscendence(){
            let temp = 0
            for (let elm of this.measures.items){
                if(elm.nom && Big(elm.nom).gte(temp)){
                    temp = elm.nom
                } else {
                    return true
                }
            }
            return false
        },
        getUncertain(){
            if(this.instrument_data && this.instrument_data.uom){
                switch(this.instrument_data.uom){
                    case "g": return "mg"
                    case "kg": return "g"
                }
            }
            return ""
        },
    },
    watch: {
        "measures.items": {
            deep: true,
            handler(){
                if(!this.preview && this.measures.items.length > 0){
                    this.calculate_max_error()
                    this.measures.result = this.calculate_result()
                }
            }
        }
    },
}
</script>